import { Component } from '@theme/component';

/**
 * Fetches a product card via the Section Rendering API and returns the inner HTML.
 * @param {string} handle
 * @returns {Promise<string|null>}
 */
async function fetchProductCard(handle) {
  const url = `/products/${encodeURIComponent(handle)}?section_id=rte-product-card`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load product: ${handle} (${res.status})`);
  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const inner = doc.querySelector('.rte-product-card-wrapper');
  return inner ? inner.innerHTML : null;
}

/**
 * Injects an override badge into a rendered card's badge container.
 * Reads from window.__productLabels (emitted by page-content.liquid).
 * Creates the container if the product has no labels of its own.
 * @param {HTMLElement} card
 * @param {string} badgeHandle
 */
function injectBadge(card, badgeHandle) {
  const labels = /** @type {Record<string, {text:string,bg:string,fg:string}>} */ (
    window['__productLabels'] || {}
  );
  const label = labels[badgeHandle];
  if (!label) {
    console.warn(`[rte-formatter] badge not found: ${badgeHandle}`);
    return;
  }

  let container = card.querySelector('.product-badges');
  if (!container) {
    container = document.createElement('div');
    container.className = 'product-badges';
    container.style.padding = '4px 0 0';
    // Insert after gallery, before title
    const title = card.querySelector('.product-title');
    if (title) title.before(container);
    else card.querySelector('.product-card__content')?.prepend(container);
  }

  const badge = document.createElement('div');
  badge.className = 'product-badges__badge product-badges__badge--rectangle product-badges__badge--metaobject';
  badge.style.backgroundColor = label.bg;
  badge.style.color = label.fg;
  badge.textContent = label.text;
  container.prepend(badge);
}

/**
 * Resolves a single <product-embed> element: shows shimmer, fetches, replaces.
 * If a badge attribute is present, injects it client-side after render.
 * @param {HTMLElement} embed
 * @returns {Promise<HTMLElement|null>}
 */
async function resolveEmbed(embed) {
  const handle = embed.getAttribute('handle');
  if (!handle) return null;

  embed.setAttribute('data-loading', '');

  try {
    const innerHTML = await fetchProductCard(handle);
    if (!innerHTML) return null;

    const replacement = document.createElement('div');
    replacement.className = 'rte-product-embed';
    replacement.innerHTML = innerHTML;

    const badgeHandle = embed.getAttribute('badge');
    if (badgeHandle) injectBadge(replacement, badgeHandle);

    embed.replaceWith(replacement);
    return replacement;
  } catch (err) {
    console.warn('[rte-formatter] product-embed error:', err);
    embed.removeAttribute('data-loading');
    embed.setAttribute('data-error', '');
    return null;
  }
}

/**
 * A custom element that formats rte content for easier styling.
 *
 * Handles two shortcode patterns:
 *
 * 1. Standalone embed — inline product card:
 *    <product-embed handle="my-handle" badge="label-handle"></product-embed>
 *
 * 2. Grid of embeds — renders children in a responsive CSS grid:
 *    <product-grid mobileColumns="2" desktopColumns="4">
 *      <product-embed handle="handle-a"></product-embed>
 *      <product-embed handle="handle-b" badge="editors-pick"></product-embed>
 *    </product-grid>
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.#renderProductGrids();
    this.#renderStandaloneEmbeds();
  }

  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * Finds all <product-grid> elements, applies column CSS vars from attributes,
   * then resolves all child <product-embed> elements in parallel.
   */
  #renderProductGrids() {
    const grids = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('product-grid')
    );

    for (const grid of grids) {
      const mobile = grid.getAttribute('mobileColumns') || '2';
      const desktop = grid.getAttribute('desktopColumns') || '4';
      grid.style.setProperty('--rte-grid-columns-mobile', mobile);
      grid.style.setProperty('--rte-grid-columns-desktop', desktop);

      const embeds = grid.querySelectorAll('product-embed[handle]');
      Promise.all(Array.from(embeds).map((e) => resolveEmbed(/** @type {HTMLElement} */ (e))));
    }
  }

  /**
   * Finds <product-embed> elements that are NOT inside a <product-grid>
   * and resolves them as standalone inline cards.
   */
  #renderStandaloneEmbeds() {
    const embeds = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('product-embed[handle]')
    );
    // product-grid embeds are already handled — skip any that are still in a grid
    const standalone = Array.from(embeds).filter(
      (e) => !e.closest('product-grid')
    );
    Promise.all(standalone.map(resolveEmbed));
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
